import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Input, Modal, Select } from 'Components';
import { useTenantProperties } from 'LearningManager/hooks/useTenantProperties';
import { useTenantScope } from 'LearningManager/hooks/useTenantScope';
import Button from '../Button';
import EmailTagInput from '../EmailTagInput';
import ToggleButton from '../ToggleButton';
import { SaveReportOption, SaveReportValues } from './SaveReportModal.types';
import {
	DELIVERY_FREQUENCY_OPTIONS,
	DELIVERY_LABELS,
	EMPTY_SAVE_REPORT_VALUES,
	EXPORT_FORMAT_OPTIONS,
	EXPORT_FORMAT_PLACEHOLDER,
	FREQUENCY_PLACEHOLDER,
	NAME_PLACEHOLDER,
	PROPERTY_PLACEHOLDER,
	RECIPIENTS_PLACEHOLDER,
	SAVE_REPORT_DESCRIPTION,
	SAVE_REPORT_TITLE,
	TENANT_PLACEHOLDER
} from './SaveReportModal.constants';
import styles from './SaveReportModal.module.scss';

interface SaveReportModalProps {
	visible: boolean;
	onCancel: () => void;
	onSave: (values: SaveReportValues) => void;
	defaultValues?: Partial<SaveReportValues>;
	exportFormatOptions?: SaveReportOption[];
	tenantOptions?: SaveReportOption[];
	propertyOptions?: SaveReportOption[];
	frequencyOptions?: SaveReportOption[];
}

const SaveReportModal: React.FC<SaveReportModalProps> = ({
	visible,
	onCancel,
	onSave,
	defaultValues,
	exportFormatOptions = EXPORT_FORMAT_OPTIONS,
	tenantOptions,
	propertyOptions,
	frequencyOptions = DELIVERY_FREQUENCY_OPTIONS
}) => {
	const {
		canSelectTenant,
		tenantOptions: storeTenantOptions,
		isLoadingTenants,
		fetchTenants,
		scopedTenant
	} = useTenantScope();
	const {
		propertyOptions: storePropertyOptions,
		isLoadingProperties,
		fetchProperties
	} = useTenantProperties();
	const [values, setValues] = useState<SaveReportValues>(
		EMPTY_SAVE_REPORT_VALUES
	);

	useEffect(() => {
		if (visible) {
			setValues({
				...EMPTY_SAVE_REPORT_VALUES,
				...(canSelectTenant ? undefined : { tenant: scopedTenant }),
				...defaultValues
			});
		}
	}, [visible, defaultValues, canSelectTenant, scopedTenant]);

	useEffect(() => {
		if (visible && canSelectTenant && !tenantOptions) {
			fetchTenants();
		}
	}, [visible, canSelectTenant, tenantOptions, fetchTenants]);

	// Properties belong to a tenant, so the list follows whichever one is in
	// play — picked by a tenant admin, or the client's own.
	useEffect(() => {
		if (visible && values.tenant && !propertyOptions) {
			fetchProperties(values.tenant);
		}
	}, [visible, values.tenant, propertyOptions, fetchProperties]);

	const updateValue = <Key extends keyof SaveReportValues>(
		key: Key,
		value: SaveReportValues[Key]
	) => setValues((current) => ({ ...current, [key]: value }));

	// A property from the previous tenant would no longer be a valid choice.
	const changeTenant = (tenant: string) =>
		setValues((current) => ({ ...current, tenant, property: undefined }));

	const trimmedName = values.name.trim();
	const isDeliveryComplete =
		!values.automaticDelivery ||
		Boolean(values.deliveryFrequency && values.recipients.length);
	const canSave = Boolean(
		trimmedName &&
			values.exportFormat &&
			values.property &&
			(!canSelectTenant || values.tenant) &&
			isDeliveryComplete
	);

	return (
		<Modal
			className={styles.root}
			visible={visible}
			onCancel={onCancel}
			centered
			size="xs"
			footer={
				<div className={styles.footer}>
					<Button
						variant="borderless"
						size="small"
						onClick={onCancel}
					>
						Close
					</Button>
					<Button
						size="small"
						disabled={!canSave}
						onClick={() => onSave({ ...values, name: trimmedName })}
					>
						{SAVE_REPORT_TITLE}
					</Button>
				</div>
			}
		>
			<div className={styles.title}>{SAVE_REPORT_TITLE}</div>
			<div className={styles.description}>{SAVE_REPORT_DESCRIPTION}</div>

			<div className={styles.field}>
				<FieldLabel
					htmlFor="lms-save-report-name"
					text="Report Name"
					required
				/>
				<Input
					id="lms-save-report-name"
					className={styles.input}
					value={values.name}
					onChange={(event) =>
						updateValue('name', event.target.value)
					}
					placeholder={NAME_PLACEHOLDER}
				/>
			</div>

			<div className={styles.field}>
				<FieldLabel text="Export Format" required />
				<Select
					className={styles.select}
					value={values.exportFormat}
					onChange={(value) => updateValue('exportFormat', value)}
					placeholder={EXPORT_FORMAT_PLACEHOLDER}
					options={exportFormatOptions}
				/>
			</div>

			<div className={styles.fieldRow}>
				{canSelectTenant && (
					<div className={styles.field}>
						<FieldLabel text="Tenant" required />
						<Select
							className={styles.select}
							value={values.tenant}
							onChange={changeTenant}
							placeholder={TENANT_PLACEHOLDER}
							options={tenantOptions ?? storeTenantOptions}
							loading={isLoadingTenants}
							showSearch
							optionFilterProp="label"
						/>
					</div>
				)}

				<div className={styles.field}>
					<FieldLabel text="Property" required />
					<Select
						className={styles.select}
						value={values.property}
						onChange={(value) => updateValue('property', value)}
						placeholder={PROPERTY_PLACEHOLDER}
						options={propertyOptions ?? storePropertyOptions}
						disabled={!values.tenant}
						loading={isLoadingProperties}
						showSearch
						optionFilterProp="label"
					/>
				</div>
			</div>

			<div className={styles.field}>
				<FieldLabel text="Automatic Delivery" />
				<div className={styles.delivery}>
					<ToggleButton
						size="small"
						checked={values.automaticDelivery}
						onChange={(checked) =>
							updateValue('automaticDelivery', checked)
						}
					/>
					<span className={styles.deliveryState}>
						{values.automaticDelivery
							? DELIVERY_LABELS.on
							: DELIVERY_LABELS.off}
					</span>
				</div>
			</div>

			{values.automaticDelivery && (
				<>
					<div className={styles.field}>
						<FieldLabel text="Delivery Frequency" required />
						<Select
							className={styles.select}
							value={values.deliveryFrequency}
							onChange={(value) =>
								updateValue('deliveryFrequency', value)
							}
							placeholder={FREQUENCY_PLACEHOLDER}
							options={frequencyOptions}
						/>
					</div>

					<div className={styles.field}>
						<FieldLabel
							htmlFor="lms-save-report-recipients"
							text="Recipients"
							required
						/>
						<EmailTagInput
							id="lms-save-report-recipients"
							value={values.recipients}
							onChange={(recipients) =>
								updateValue('recipients', recipients)
							}
							placeholder={RECIPIENTS_PLACEHOLDER}
						/>
					</div>
				</>
			)}
		</Modal>
	);
};

interface FieldLabelProps {
	text: string;
	required?: boolean;
	htmlFor?: string;
}

const FieldLabel: React.FC<FieldLabelProps> = ({ text, required, htmlFor }) => (
	<label className={styles.label} htmlFor={htmlFor}>
		{text}
		{required && (
			<span className={styles.required} aria-hidden="true">
				*
			</span>
		)}
	</label>
);

export default observer(SaveReportModal);
